/**
 * Reset a password — MOVED, unchanged.
 *
 * The plaintext is returned ONCE by the server and shown once here,
 * because the builder has no SMTP and cannot mail it. The copy button's
 * `catch` is deliberately empty and deliberately silent about itself:
 * on an http origin the clipboard API is unavailable, and the recovery
 * is that the password stays on screen and selectable. Closing the modal
 * loses it, and the warning line says so.
 */

import { Button, Modal } from "@omnitok/ui";
import { AlertTriangle, Copy, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import { adminUsersApi, type UserAdmin } from "@/lib/admin-users-api";

export function ResetPasswordModal({
  user,
  onClose,
}: {
  user: UserAdmin | null;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useT();
  const [password, setPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id ?? null;
  useEffect(() => {
    setPassword(null);
    setError(null);
    setCopied(false);
  }, [userId]);

  if (user === null) return null;

  const reset = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setPassword((await adminUsersApi.resetPassword(user.id)).password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (password === null) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Unavailable on an http origin. The plaintext stays visible and
         selectable, which is the whole recovery. */
    }
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      title={t("access.people.reset.title")}
      size="md"
      footer={
        password === null ? (
          <div className="flex justify-end gap-2">
            <Button
              ghost
              size="sm"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={busy}
              disabled={busy}
              leftIcon={<RefreshCw size={14} />}
              onClick={() => void reset()}
            >
              {t("access.people.reset.generate")}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button
              ghost
              size="sm"
              leftIcon={<Copy size={14} />}
              onClick={() => void copy()}
            >
              {copied ? t("common.copied") : t("common.copy")}
            </Button>
            <Button variant="primary" size="sm" onClick={onClose}>
              {t("access.people.reset.done")}
            </Button>
          </div>
        )
      }
    >
      <p className="text-sm text-neutral-600">
        {t("access.people.reset.prompt", { name: user.name })}
      </p>

      {error !== null && (
        <p className="mt-3 whitespace-pre-wrap rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark">
          {error}
        </p>
      )}

      {password !== null && (
        <>
          <div className="mt-3 rounded-lg border border-primary/30 bg-primary-soft p-3">
            <p className="text-xs font-semibold text-primary">
              {t("access.people.reset.newPassword")}
            </p>
            <code className="mt-1 block break-all font-mono text-sm text-neutral-900">
              {password}
            </code>
          </div>
          <p className="mt-2 text-xs text-warning-dark">
            <AlertTriangle size={12} className="mr-1 inline align-text-bottom" />
            {t("access.people.reset.warning")}
          </p>
        </>
      )}
    </Modal>
  );
}
